/* eslint-disable complexity */
import React from 'react'
import {
  Box,
  Divider,
  Stack,
  Typography,
  type BoxProps,
  type SxProps,
} from '@mui/material'
import { Icon, Image } from '@media-components'
import { ActionButton } from '@components/common/ActionButton'
import { publicImages } from '@utils/shared'
import {
  BaselineStyleClasses,
  commonStyles,
  CustomStyles,
  getStyles,
} from '@utils/styles'
import { LinkClick } from '../../LinkClick'
import { CardData } from '../DefaultCard'
import defaultStyles, { StylesClassNames } from './styles'

export type PreviewCardData = CardData

export type PreviewCardProps = PreviewCardData & {
  csx?: CustomStyles<StylesClassNames>
  componentVariant?: BoxProps['component']
  forBlogs?: boolean
  hideCta?: boolean
  actionButtonIconSx?: SxProps
  disableDescriptionLineClamp?: boolean
}

export function PreviewCard({
  csx,
  imageData = {
    src: publicImages.previewImageCard,
    alt: 'img',
  },
  chipsData,
  title,
  description,
  actionButtonData,
  statsData,
  componentVariant = 'h2',
  forBlogs = false,
  hideCta = false,
  href,
  actionButtonIconSx,
  disableDescriptionLineClamp = false,
}: PreviewCardProps) {
  const styles = getStyles(defaultStyles, csx)

  const finalSlug = forBlogs
    ? `/${actionButtonData?.link}`
    : actionButtonData?.link
  const cardLink = href?.trim()
  const finalHref = actionButtonData?.link ? finalSlug : cardLink

  const hasLink = Boolean(finalHref) && !finalHref?.includes('undefined')

  return (
    <Box
      {...styles('wrapper', {
        ...(hasLink && {
          '&:hover .preview-card-title': {
            textDecoration: 'underline',
          },
        }),
      })}
      className={BaselineStyleClasses.imageTransitioningWrapper}
    >
      <LinkClick link={hasLink ? finalHref : undefined}>
        <Image
          {...imageData}
          {...styles('image')}
          style={{ transition: 'all 0.6s ease-in-out' }}
          className={BaselineStyleClasses.imageTransition}
        />
        <Box {...styles('gradientCover')} />
        <Box {...styles('tagsWrapper')}>
          {chipsData?.map(({ label }, idx) => (
            <Box {...styles('tag')} key={idx}>
              <Typography variant="body2" key={label}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box {...styles('info')}>
          <Stack {...styles('infoWrapper')}>
            <Stack {...styles('textWrapper')}>
              {title && (
                <Box {...styles('titleRow')}>
                  <Typography
                    variant="h6.600"
                    color="neutral.white"
                    component={
                      (statsData && statsData.length) || description
                        ? componentVariant
                        : 'span'
                    }
                    {...styles('title')}
                    className="preview-card-title"
                  >
                    {title}
                  </Typography>
                  <Icon
                    iconName="ArrowRight"
                    fontSize="small"
                    colorValues="neutral.white"
                    {...styles('titleIcon')}
                  />
                </Box>
              )}
              {description && (
                <Typography
                  variant="body1.500"
                  {...styles('description', {
                    ...(!disableDescriptionLineClamp && {
                      ...commonStyles.getLineClampStyles(1, 24),
                    }),
                  })}
                >
                  {description}
                </Typography>
              )}
            </Stack>

            {!hideCta && actionButtonData && actionButtonData.children && (
              <div>
                <ActionButton
                  {...actionButtonData}
                  link={undefined}
                  showArrow
                  iconSx={actionButtonIconSx}
                />
              </div>
            )}
          </Stack>
          {statsData && (
            <Box {...styles('statsCardWrapper')}>
              {statsData.map(({ label, description }, idx) => (
                <React.Fragment key={`${label}-${description}-${idx}`}>
                  <Stack>
                    <Typography variant="h6.700" color="common.white">
                      {label}
                    </Typography>
                    <Typography variant="body1.500" color="common.white">
                      {description}
                    </Typography>
                  </Stack>
                  {idx === 0 && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ backgroundColor: 'background.paperDark' }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>
          )}
        </Box>
      </LinkClick>
    </Box>
  )
}
